import { useState, useEffect } from 'react';
import { Modal, Form } from 'react-bootstrap'; // Import Form for the toggle switch
import { useModal } from '../providers/modal-provider.js';
import swal from 'sweetalert';
import BarcodeScannerComponent from "react-qr-barcode-scanner";

export default function ProductQrReaderModal() {
  const { displayModal, modalData, closeModal, setModalData, setModalView } = useModal();
  const [selectedProductList, setSelectedProductList] = useState([]);
  const [multipleCaptureEnabled, setMultipleCaptureEnabled] = useState(true); // New state for multiple capture
  const [selectedCamera, setSelectedCamera] = useState("user"); // State for camera selection (default to back camera)

  // Function to generate a simple beep sound using Web Audio API
  const playBeepSound = () => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();

      // Check if context is suspended and resume it
      if (audioContext.state === 'suspended') {
        audioContext.resume();
      }

      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.type = 'sine'; // 'sine' is generally clear; 'square' can be sharper
      oscillator.frequency.value = 880; // Increase frequency to a higher, more distinct pitch (e.g., 880Hz for A5)
      gainNode.gain.setValueAtTime(0.7, audioContext.currentTime); // Increase initial volume (e.g., to 0.7 or 0.8)

      const duration = 0.25; // Increase duration to 0.25 seconds (250ms)

      oscillator.start();
      oscillator.stop(audioContext.currentTime + duration);

      // Stronger fade out for a crisp stop
      gainNode.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + duration);

      // Optional: Ensure context closes if it's a one-off sound
      // This can prevent resource leaks if many sounds are played quickly,
      // but might introduce a slight delay if recreated often.
      // If playing many beeps, keep the context open and reuse.
      // For a single beep per scan, closing is fine.
      oscillator.onended = () => {
        audioContext.close();
      };

    } catch (e) {
      console.error("Web Audio API not supported or error playing sound:", e);
    }
  };

  const captureProductQr = (err, result) => {
    if (result) {
      playBeepSound(); // Play the beep sound immediately after a successful scan

      const scannedText = result.text;

      const pidMatch = scannedText.match(/Pid:([^\|]+)/);
      const pnameMatch = scannedText.match(/Pname:([^\|]+)/);
      const serialUidMatch = scannedText.match(/Serial_uid:([^\|]+)/);
      const batchIdMatch = scannedText.match(/Batch_uid:([^\|]+)/);
      const serialMatch = scannedText.match(/Serial:([^\|]+)/); // Actual serial number for display
      const batchMatch = scannedText.match(/Batch:([^\|]+)/);   // Actual batch number for display

      if (pidMatch && pnameMatch) {
        const pid = pidMatch[1].trim();
        const pname = pnameMatch[1].trim();
        const serial_uid = serialUidMatch ? serialUidMatch[1].trim() : null;
        const batch_id = batchIdMatch ? batchIdMatch[1].trim() : null;
        const serial = serialMatch ? serialMatch[1].trim() : null;
        const batch = batchMatch ? batchMatch[1].trim() : null;

        // Determine if the scanned QR is Type 1 (tracked) or Type 2 (untracked)
        const isScannedType1 = (serial_uid !== null || batch_id !== null);

        const newProduct = {
          pid,
          pname,
          serial_uid, // Unique identifier for serial-tracked items
          batch_id,   // Unique identifier for batch-tracked items
          serial,     // Display serial
          batch,      // Display batch
          quantity: 1 // Always 1 per scan as per requirement
        };

        setSelectedProductList(prevList => {
          let updatedList = [...prevList];

          // Check for Scenario B: Exact Type 1 duplicate (same PID AND same serial_uid/batch_id)
          if (isScannedType1) {
            const isExactType1Duplicate = updatedList.some(item =>
              item.pid === pid &&
              ((serial_uid && item.serial_uid === serial_uid) || (batch_id && item.batch_id === batch_id))
            );

            if (isExactType1Duplicate) {
              swal("Duplicate", "This specific serial/batch product has already been scanned!", "warning");
              return prevList; // Prevent adding duplicate Type 1 product
            }
          }

          // Check for Scenario A: Type 1 product already exists, and a Type 2 product is being scanned
          // Or if a Type 2 product already exists, and a Type 1 product is being scanned.
          // This covers the case where you can't mix tracked and untracked of the same PID.
          const existingProductWithSamePid = updatedList.find(item => item.pid === pid);

          if (existingProductWithSamePid) {
            const isExistingType1 = (existingProductWithSamePid.serial_uid !== null || existingProductWithSamePid.batch_id !== null);

            if (isScannedType1 && !isExistingType1) {
              // Trying to add a Type 1 product when a Type 2 of the same PID exists
              swal("Duplicate Conflict", `Product "${pname}" (PID: ${pid}) is already scanned as an untracked item. Cannot add its tracked version.`, "warning");
              return prevList;
            } else if (!isScannedType1 && isExistingType1) {
              // Trying to add a Type 2 product when a Type 1 of the same PID exists
              swal("Duplicate Conflict", `Product "${pname}" (PID: ${pid}) is already scanned as a tracked item. Cannot add its untracked version.`, "warning");
              return prevList;
            }
          }

          // If no duplicate scenarios are met, add the new product.
          // Since quantity is always 1 and there's no incrementing, just add.
          if (multipleCaptureEnabled) {
            return [...updatedList, newProduct];
          } else {
            // If multipleCaptureEnabled is false, immediately close modal and complete scan
            closeModal();
            if (modalData.onScanComplete) {
              modalData.onScanComplete([newProduct]);
            }
            return [newProduct]; // Replace list with only the new product
          }
        });
      } else {
        swal("Invalid QR", "Could not extract Product ID and Name from QR code", "error");
      }
    }
  };

  // Function to remove a serial number from the list
  const removeProductList = (indexToRemove) => {
    setSelectedProductList(prevSerials =>
      prevSerials.filter((_, index) => index !== indexToRemove)
    );
  };

  // Function to handle modal close
  const handleProductQrModalClose = () => {
    setSelectedProductList([]); // Clear selected serials on close
    //setMultipleCaptureEnabled(false); // Reset multiple capture state
    closeModal();
    if (modalData.onScanComplete && !multipleCaptureEnabled) {
      // If modal is closed without processing and multiple capture was OFF,
      // it means no serial was captured, or the user cancelled a single capture.
      // You might want to handle this case, e.g., by returning an empty array or null.
      // For now, no callback is made here as the user might just be closing.
    }
  };

  // Function to handle "Process" button click (when multiple capture is enabled)
  const handleProcessScannedSerials = () => {
    closeModal();
    if (modalData.onScanComplete) {
      const summarizedData = {};

      selectedProductList.forEach(product => {
        if (summarizedData[product.pid]) {
          summarizedData[product.pid].quantity += product.quantity; // product.quantity is always 1 here
        } else {
          summarizedData[product.pid] = {
            pid: product.pid,
            quantity: product.quantity
          };
        }
      });

      const finalSummaryArray = Object.values(summarizedData);
      modalData.onScanComplete(finalSummaryArray);
    }
    setSelectedProductList([]); // Clear after processing
    //setMultipleCaptureEnabled(false); // Reset multiple capture state
  };

  return (
    <Modal show={displayModal} onHide={handleProductQrModalClose} size="md" centered>
      <Modal.Header closeButton> {/* Added closeButton prop for default close icon */}
        <Modal.Title>{modalData.product_name || "Scan Product QR"}</Modal.Title>
        {/* Toggle button for multiple capture */}
        <div className="d-flex align-items-center ms-auto me-3">
          <span className="me-2 text-gray-600 fw-bold">Multiple Capture:</span>
          <Form.Check
            type="switch"
            id="multiple-capture-switch"
            checked={multipleCaptureEnabled}
            disabled
            onChange={(e) => { console.log(e.target.checked); setMultipleCaptureEnabled(() => { return e.target.checked }); }}
          />
        </div>
      </Modal.Header>
      <Modal.Body>
        <div className='container px-1'>
          <div className='row align-items-center mt-5'>
            <div className='col-md-5 mt-5 mt-md-0'>
              <div className='mx-auto'>
                <BarcodeScannerComponent
                  width={350}
                  height={350}
                  onUpdate={captureProductQr}
                  videoConstraints={{
                    width: 1280,
                    height: 720,
                    facingMode: selectedCamera === "user" ? selectedCamera : { exact: selectedCamera }
                  }}
                />
                {/* Display captured serial numbers only if multiple capture is enabled and there are items */}
                {multipleCaptureEnabled && selectedProductList.length > 0 && (
                  <>
                    <h3 className="fw-bold m-0 mt-3">Selected Products:</h3>
                    <div className="d-flex align-items-center gap-2 gap-lg-3 flex-wrap mt-2">
                      {selectedProductList.map((obj, index) => (
                        <div key={index} className="btn btn-sm btn-light d-flex align-items-center px-2">
                          <div className="text-gray-600 fw-bold">
                            {obj.pname}
                            {obj.serial && ` (S/N: ${obj.serial})`} {/* Display serial if present */}
                            {obj.batch && ` (Batch: ${obj.batch})`} {/* Display batch if present */}
                          </div>
                          <button
                            type="button"
                            style={{ width: "auto", height: "auto", marginLeft: "1rem" }}
                            onClick={() => removeProductList(index)}
                            className="btn btn-clean btn-sm btn-icon btn-icon-primary btn-active-light-primary"
                          >
                            <span className="svg-icon svg-icon-4">
                              <svg xmlns="[http://www.w3.org/2000/svg](http://www.w3.org/2000/svg)" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="feather feather-x-circle">
                                <circle cx="12" cy="12" r="10"></circle>
                                <line x1="15" y1="9" x2="9" y2="15"></line>
                                <line x1="9" y1="9" x2="15" y2="15"></line>
                              </svg>
                            </span>
                          </button>
                        </div>
                      ))}
                    </div>
                  </>
                )}

                <div className='col-md-2 row mt-5 px-5' style={{ zIndex: 1 }} >
                  <div className='col-3 col-md-12 d-flex align-items-center'>
                    <button className="btn rounded-pill" onClick={() => { selectedCamera == "user" ? setSelectedCamera("environment") : setSelectedCamera("user") }}>
                      <svg version="1.1" width="32" height="32" viewBox="0 0 1000 1000">
                        <g transform="translate(0.000000,380.000000) scale(0.100000,-0.100000)">
                          <path d="M3257.7,2389.6c-99.6-51.7-227.8-197.2-390.6-444.2c-225.9-340.8-55.5-300.6-1294.3-306.3l-1062.6-5.7l-103.4-57.4c-59.4-32.5-135.9-97.6-178.1-149.3c-139.8-178.1-132.1,19.1-126.4-3049.9l5.7-2755.1l40.2-88.1c88.1-189.6,247-308.3,461.4-342.7c149.3-24.9,8630.9-24.9,8780.3,0c214.4,34.5,373.3,153.2,461.4,342.7l40.2,88.1l5.7,2755.1c5.7,3069.1,13.4,2871.9-126.3,3049.9c-42.1,51.7-118.7,116.8-178,149.3l-103.4,57.4l-1062.6,5.7c-1238.7,5.8-1068.3-34.5-1294.3,306.3c-166.6,250.8-291,394.4-394.4,446.1c-68.9,34.5-135.9,36.4-1742.3,36.4C3403.2,2426,3320.9,2424.1,3257.7,2389.6z M6621.7,1983.7c30.6-34.5,128.3-168.5,214.4-296.8c162.7-243.1,268-354.2,377.2-400.1c51.7-21.1,254.6-26.8,1102.8-26.8c999.4,0,1041.5-1.9,1100.9-38.3c34.5-21.1,68.9-61.3,76.6-90c9.6-28.7,15.3-1259.8,15.3-2734c0-2552.1-1.9-2684.3-34.5-2734c-19.1-30.6-55.5-61.3-82.3-70.8c-65.1-24.9-8719-24.9-8784.1,0c-26.8,9.6-63.2,40.2-82.3,70.8c-32.5,49.8-34.5,181.9-34.5,2734c0,1474.2,5.7,2705.3,15.3,2734c7.7,28.7,42.1,68.9,76.6,90c59.4,36.4,101.5,38.3,1100.9,38.3c848.2,0,1051.1,5.7,1102.8,26.8c109.1,45.9,214.4,157,377.2,400.1c86.2,128.3,183.8,262.3,214.4,296.8l59.4,61.3H5000h1562.3L6621.7,1983.7z"></path>
                          <path d="M4779.8,461.6c-254.6-30.6-538-126.4-758.2-256.6C3870.4,117,3832.1,71,3832.1-24.7c0-91.9,59.4-164.7,145.5-181.9c55.5-9.6,99.5,3.8,281.4,93.8c292.9,145.5,381,168.5,673.9,180c210.6,7.7,275.7,3.8,421.2-30.6c492.1-118.7,896-459.5,1089.4-917.1c47.9-113,88.1-262.3,116.8-434.6l11.5-76.6l-149.3,145.5c-178.1,174.2-227.8,208.7-296.8,208.7c-124.4,0-225.9-113-199.1-222.1c9.6-36.4,151.3-193.4,428.9-472.9L6771-2148l388.7,386.7c214.4,214.4,402.1,411.6,417.4,440.4c42.1,82.3,32.5,157-28.7,224c-45.9,47.9-70.8,59.4-135.9,59.4c-78.5,0-91.9-9.6-268-181.9l-185.7-183.8l-11.5,124.5c-46,482.5-325.5,997.5-716.1,1313.4C5830.9,358.2,5270,522.9,4779.8,461.6z"></path><path d="M2811.6-1052.8c-365.7-367.6-415.5-425-415.5-478.6c0-88.1,36.4-151.3,105.3-185.7c34.5-15.3,72.8-28.7,86.2-28.7c53.6,0,155.1,72.8,298.7,214.4l155.1,153.2l11.5-227.8c17.2-323.6,90-570.5,250.8-853.9c275.7-482.5,752.4-830.9,1296.2-943.9c218.3-47.9,585.9-47.9,800.3-1.9c225.9,47.9,465.2,149.3,664.3,279.5c229.8,151.2,298.7,225.9,302.5,327.4c1.9,68.9-7.7,88.1-63.2,137.9c-91.9,82.3-174.2,68.9-342.7-61.3c-622.2-480.6-1510.6-425-2067.8,130.2c-281.4,279.6-461.4,708.4-463.3,1093.3v124.4l178.1-174.2c101.5-99.6,201-181.9,231.7-189.5c122.5-30.6,264.2,111,233.6,233.6c-9.6,36.4-153.2,195.3-419.3,463.3c-222.1,222.1-409.7,405.9-415.5,405.9C3232.8-635.4,3041.4-823.1,2811.6-1052.8z"></path>
                        </g>
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className="justify-content-between">
        <button
          className="btn btn-secondary"
          onClick={handleProductQrModalClose}
        >
          Close
        </button>

        {/* Show Process button only if multiple capture is enabled and items are selected */}
        {multipleCaptureEnabled && selectedProductList.length > 0 && (
          <button
            className="btn btn-primary"
            onClick={handleProcessScannedSerials}
          >
            Process ({selectedProductList.length})
          </button>
        )}

      </Modal.Footer>
    </Modal>
  );
}